import { createUseStyles } from '@glu/theming';
import React, { useRef, useEffect } from 'react';
import { Button, PRIMARY, SECONDARY, SMALL } from '@glu/buttons-react';
import locale from '@glu/locale';
import styles from './Actions.styles';
import { DropdownActions, DropdownProps } from './Dropdown';
import { State } from './DropdownContext';

const useStyles = createUseStyles(styles);

interface Props {
  applySelection: DropdownActions['applySelection'];
  btnClearEnabled?: DropdownProps['btnClearEnabled'];
  btnDoneEnabled?: DropdownProps['btnDoneEnabled'];
  btnSelectAllEnabled?: DropdownProps['btnSelectAllEnabled'];
  /** Whether to make this option the active element */
  focused?: boolean;
  onClear?: DropdownProps['onClear'];
  onSelectAll?: DropdownProps['onSelectAll'];
  options: DropdownProps['options'];
  selection: State['selection'];
  setSelection: DropdownActions['setSelection'];
}

const Actions = ({
  applySelection,
  btnClearEnabled,
  btnDoneEnabled,
  btnSelectAllEnabled,
  focused,
  onClear,
  onSelectAll,
  options,
  selection,
  setSelection,
}: Props) => {
  const classes = useStyles();

  const handleClear = () => {
    setSelection(selection.filter(({ disabled }) => disabled));
    onClear();
  };
  const handleSelectAll = () => {
    setSelection([
      ...options.filter(({ disabled }) => !disabled),
      ...selection.filter(({ disabled }) => disabled),
    ]);
    onSelectAll();
  };

  const buttonRef = useRef(null);

  useEffect(() => {
    if (focused) {
      buttonRef.current.querySelector('button')?.focus();
    }
  });

  return (
    <div ref={buttonRef} className={classes.root}>
      {btnDoneEnabled && (
        <Button
          className={classes.button}
          data-qa="button-done"
          onClick={() => {
            applySelection(selection);
          }}
          size={SMALL}
          text={locale.get('done')}
          type="button"
          variant={PRIMARY}
        />
      )}
      {(btnSelectAllEnabled || btnClearEnabled) && (
        <>
          {btnSelectAllEnabled && (
            <Button
              className={classes.button}
              data-qa="button-select-all"
              onClick={handleSelectAll}
              size={SMALL}
              text={locale.get('selectall')}
              type="button"
              variant={SECONDARY}
            />
          )}
          {btnClearEnabled && (
            <Button
              className={classes.button}
              data-qa="button-clear"
              onClick={handleClear}
              size={SMALL}
              text={locale.get('clear')}
              type="button"
              variant={SECONDARY}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Actions;
